import {
  ChartEntryMetadata,
  newChartEntryMetadata,
} from '@decisions/server/DecisionsFramework/Design/Charts/ChartEntryMetadata';
import { ChartMetadataBehavior } from '@decisions/server/DecisionsFramework/Design/Charts/ChartMetadataBehavior';
import { ChartMetadataType } from '@decisions/server/DecisionsFramework/Design/Charts/ChartMetadataType';
import { swap } from 'utils/ArrayUtils';

export interface IChartEntryFilterState {
  selectedRows: number[];
  entries: ChartEntryMetadata[];
  originalEntries?: Map<string, ChartEntryMetadata>;
  multiSelect: boolean;
  multiSelectActions: boolean;
}

export const chartEntryFilterReducer: React.Reducer<IChartEntryFilterState, EntryFilterAction | undefined> = (
  state: IChartEntryFilterState,
  action?: EntryFilterAction
) => {
  if (!action) {
    if (!state.originalEntries || state.originalEntries.size < state.entries.length) {
      // initialize original entry set:
      // might need a deep copy here, because we need to put the data ones back how they were...
      return { ...state, originalEntries: getEntriesByKey(state.entries) };
    }
    // don't do anything if we somehow end up here... / make TS happy.
    return state;
  }

  let entries = [...state.entries];
  state.originalEntries = state.originalEntries ?? getEntriesByKey(state.entries);
  let entry = entries[action.index];

  switch (action.type) {
    case 'Set Entries':
      entries = [...(action.entries as ChartEntryMetadata[])];
      return { ...state, entries, originalEntries: getEntriesByKey(entries) }; // might need a deep copy here, because we need to put the data ones back how they were...
    case 'SelectRow':
      if (entry.Grouped?.length) {
        // prevent selection of groups, because we only want one level of nesting.
        return state;
      }
      let selectedRows = [action.index];
      if (action.isMulti || state.multiSelect) {
        const i = state.selectedRows.indexOf(action.index);
        selectedRows = [...state.selectedRows];
        if (i !== -1) {
          selectedRows.splice(i, 1);
        } else {
          selectedRows.push(action.index);
        }
      }
      return { ...state, selectedRows };

    case 'ToggleVisibility':
      entries[action.index] = { ...entry, Visible: !entry.Visible };
      return { ...state, entries };

    case 'Update Label':
      entries[action.index] = {
        ...entry,
        LabelText: action.label,
        Key: entry.Key || action.label, // set key to label if creating new
        Type: ChartMetadataType.UserDefined,
      };
      return { ...state, entries };

    case 'Update Color':
      entries[action.index] = {
        ...entry,
        Color: action.color,
        Type: ChartMetadataType.UserDefined,
      };
      return { ...state, entries };

    // Move/Up down will also make an entry UserDefined, and will adjust _even_ SortIndexes up/down
    // in surrounding elements. Data-generated entries will not be adjusted and keep their odd indexes.
    // This is to support the "remove" behavior that needs to restore elements to their original state.
    case 'Move Up':
      if (action.index === 0) {
        return state;
      }

      return { ...state, entries: swapEntries(entries, action.index, true), selectedRows: [] };

    case 'Move Down':
      if (action.index === entries.length - 1) {
        return state;
      }

      return { ...state, entries: swapEntries(entries, action.index, false), selectedRows: [] };

    case 'Add Row':
      entries.push(getNew(entries.length));
      return { ...state, entries };

    case 'Cancel Add Row':
      entries.pop();
      return { ...state, entries };

    case 'Group':
      // console.log(entries);
      let grouped: ChartEntryMetadata[] = [];
      (state.selectedRows as number[]).forEach(i => {
        grouped.push(entries[i]);
      });
      grouped.forEach(item => {
        entries.splice(entries.indexOf(item), 1);
      });
      entries.push(getNew(entries.length, grouped));
      return { ...state, entries, selectedRows: [], multiSelect: false, multiSelectActions: false };

    case 'Un Group':
      const { groupKey, index: groupedIndex } = action;
      const groupEntry = entries.find(entry => entry.Key === groupKey);
      if (!groupEntry) {
        return state;
      }
      const group = groupEntry.Grouped ? [...groupEntry.Grouped] : [];
      const unGrouped = group.splice(groupedIndex, 1)[0];
      entries = [...entries, unGrouped].sort(entryComparator);
      groupEntry.Grouped = group;
      if (!groupEntry.Grouped.length) {
        entries.splice(entries.indexOf(groupEntry), 1);
      }
      return { ...state, entries };

    case 'Remove':
      if (entry.Grouped?.length) {
        // if it's a group being removed, put it's entries back
        entries.splice(action.index, 1);
        entries = [...entries, ...entry.Grouped].sort(entryComparator);
      } else {
        // // change it back to dynamic

        // entry.Type = ChartMetadataType.Dynamic; // seems this distinction should only matter _server side_
        // remove the user defined one
        entries.splice(action.index, 1);

        const original = state.originalEntries.get(entry.Key);
        if (original && original.Type === ChartMetadataType.Dynamic) {
          // restore the original if it exists and is from the data (dynamic):
          entries.push(original);
          // this sorting might not put it back (_right_ where it was, because the reordering also would modify neighbors)
          entries.sort(entryComparator).forEach((sorted, i) => (sorted.SortIndex = i)); // fix up sort indexes to avoid duplicates
        }
      }

      return { ...state, entries };

    case 'Toggle Multi Select':
      const multiSelect = true;
      const multiSelectActions = state.multiSelect && !state.multiSelectActions;
      return { ...state, multiSelect, multiSelectActions };
    case 'Cancel Multi Select':
      return { ...state, multiSelect: false, multiSelectActions: false, selectedRows: [] };
    default:
      return state;
  }
};

export type EntryFilterActionTypes =
  | 'Add Row'
  | 'Cancel Add Row'
  | 'Set Entries'
  | 'SelectRow'
  | 'Group'
  | 'Un Group'
  | 'ToggleVisibility'
  | 'Update Label'
  | 'Update Color'
  | 'Move Up'
  | 'Remove'
  | 'Move Down'
  | 'Toggle Multi Select'
  | 'Cancel Multi Select';

export type EntryFilterAction = { type: EntryFilterActionTypes; [key: string]: any };

export function getNew(index: number, grouped?: ChartEntryMetadata[]) {
  return newChartEntryMetadata({
    Key: '',
    BackingDataTypeName: '',
    LabelText: '',
    Color: '',
    Visible: true,
    SortIndex: index,
    Type: ChartMetadataType.UserDefined,
    Behavior: ChartMetadataBehavior.Label, // shouldn't this be for the whole set?
    Grouped: grouped,
  });
}

function entryComparator(a: ChartEntryMetadata, b: ChartEntryMetadata) {
  const typeAdust = a.Type - b.Type;
  return a.SortIndex - (b.SortIndex - typeAdust);
}

export function getEntriesByKey(entries: ChartEntryMetadata[]) {
  const entryMap = new Map();
  entries.forEach(entry => entryMap.set(entry.Key, entry));
  return entryMap;
}

function swapEntries(entries: ChartEntryMetadata[], index: number, up: boolean) {
  const moving = { ...entries[index], Type: ChartMetadataType.UserDefined };
  const targetIndex = up ? index - 1 : index + 1;
  entries[index] = moving;
  // what if this whole thing was a map by sortIndex, instead of just an array?
  // or instead of swap and splice, we just adjusted the sortIndex and sorted by sortIndex?
  const swappedDown = entries[targetIndex];
  const movedIndex = swappedDown.SortIndex;
  swap(entries, index, targetIndex);
  swappedDown.SortIndex = moving.SortIndex;
  moving.SortIndex = movedIndex;
  return entries;
}
